import React, { useState } from 'react';
import { Users, TrendingUp, Trophy, Crown, Medal, ArrowUp } from 'lucide-react';

// Preview component to showcase different KPI card layouts
const KPICardPreview = () => {
  const [selectedOption, setSelectedOption] = useState('A');

  const data = {
    title: "Top Customer",
    customerName: "Rozella39 Mertz280",
    value: "3,551",
    subtitle: "leads with 3,551 claims",
    secondPlace: "Sung603 Weber641",
    secondValue: "2,822",
    percentageGap: "25.8%"
  };

  const options = ['A', 'B', 'C', 'D', 'E'];

  return (
    <div className="p-6 bg-slate-950 min-h-screen">
      <h2 className="text-white text-lg font-medium mb-4">KPI Card Layout Options</h2>
      
      {/* Option Selector */}
      <div className="flex gap-2 mb-6">
        {options.map(opt => (
          <button
            key={opt}
            onClick={() => setSelectedOption(opt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedOption === opt 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-800 text-gray-400 hover:text-white'
            }`}
          >
            Option {opt}
          </button>
        ))}
      </div>

      {/* Preview Area */}
      <div className="max-w-2xl">
        {/* Option A: Large Value Focus */}
        {selectedOption === 'A' && (
          <div className="rounded-lg bg-slate-900/60 border border-slate-800/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Trophy className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Top Customer</span>
            </div>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-2xl font-bold text-white font-mono">{data.value}</p>
                <p className="text-xs text-gray-400 mt-1">claims</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-white">{data.customerName}</p>
                <p className="text-[10px] text-emerald-400 flex items-center justify-end gap-1 mt-1">
                  <ArrowUp className="w-3 h-3" />
                  {data.percentageGap} above #2
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Option B: Comparison Style */}
        {selectedOption === 'B' && (
          <div className="rounded-lg bg-slate-900/60 border border-slate-800/50 p-4">
            <div className="flex items-center gap-2 mb-4">
              <Crown className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-medium text-gray-300">Customer Ranking</span>
            </div>
            <div className="space-y-3">
              {/* First Place */}
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold">1</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white font-medium">{data.customerName}</span>
                    <span className="text-sm text-white font-mono">{data.value}</span>
                  </div>
                  <div className="mt-1.5 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>
              </div>
              {/* Second Place */}
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-slate-700/50 text-gray-400 flex items-center justify-center text-xs font-bold">2</span>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{data.secondPlace}</span>
                    <span className="text-sm text-gray-400 font-mono">{data.secondValue}</span>
                  </div>
                  <div className="mt-1.5 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-600 rounded-full" style={{ width: '79%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Option C: Mini Dashboard */}
        {selectedOption === 'C' && (
          <div className="rounded-lg bg-slate-900/60 border border-slate-800/50 p-4">
            <div className="grid grid-cols-3 gap-4">
              {/* Top Customer */}
              <div className="text-center p-3 rounded-lg bg-slate-800/30">
                <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                </div>
                <p className="text-lg font-bold text-white font-mono">#1</p>
                <p className="text-[10px] text-gray-500 mt-1">Rozella39</p>
                <p className="text-[10px] text-gray-400">Mertz280</p>
              </div>
              {/* Claims Count */}
              <div className="text-center p-3 rounded-lg bg-slate-800/30">
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-2">
                  <TrendingUp className="w-4 h-4 text-blue-400" />
                </div>
                <p className="text-lg font-bold text-white font-mono">{data.value}</p>
                <p className="text-[10px] text-gray-500 mt-1">Total Claims</p>
                <p className="text-[10px] text-emerald-400">+{data.percentageGap}</p>
              </div>
              {/* Lead Gap */}
              <div className="text-center p-3 rounded-lg bg-slate-800/30">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-2">
                  <ArrowUp className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-lg font-bold text-white font-mono">729</p>
                <p className="text-[10px] text-gray-500 mt-1">Lead Over #2</p>
                <p className="text-[10px] text-gray-400">claims ahead</p>
              </div>
            </div>
          </div>
        )}

        {/* Option D: Rank Card with Badge */}
        {selectedOption === 'D' && (
          <div className="rounded-lg bg-gradient-to-r from-amber-500/10 to-transparent border border-amber-500/20 p-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Crown className="w-7 h-7 text-amber-400" />
                </div>
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-slate-900 text-xs font-bold flex items-center justify-center">#1</span>
              </div>
              <div className="flex-1">
                <p className="text-xs text-amber-400/80 font-medium uppercase tracking-wide">Top Customer</p>
                <p className="text-base font-semibold text-white mt-0.5">{data.customerName}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xl font-bold text-white font-mono">{data.value}</span>
                  <span className="text-xs text-gray-400">claims</span>
                  <span className="text-[10px] text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                    +{data.percentageGap} vs #2
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Option E: Visual Bar Style */}
        {selectedOption === 'E' && (
          <div className="rounded-lg bg-slate-900/60 border border-slate-800/50 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Medal className="w-4 h-4 text-amber-400" />
                <span className="text-xs font-medium text-gray-300">Top Customer by Claims</span>
              </div>
              <span className="text-[10px] text-gray-500">Total: {data.value} claims</span>
            </div>
            <div className="relative">
              <div className="h-12 bg-slate-800 rounded-lg overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-amber-500 to-amber-600 rounded-lg flex items-center justify-between px-4"
                  style={{ width: '100%' }}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold text-white">1</span>
                    <span className="text-sm font-medium text-white">{data.customerName}</span>
                  </div>
                  <span className="text-lg font-bold text-white font-mono">{data.value}</span>
                </div>
              </div>
              {/* Runner up indicator */}
              <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500 px-1">
                <span>Runner-up: {data.secondPlace}</span>
                <span>{data.secondValue} claims ({data.percentageGap} behind)</span>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        <div className="mt-4 p-3 bg-slate-800/30 rounded-lg">
          <p className="text-xs text-gray-400">
            {selectedOption === 'A' && "Large Value Focus: Emphasizes the claim count with customer name as secondary info."}
            {selectedOption === 'B' && "Comparison Style: Shows ranking with progress bars comparing top 2 customers."}
            {selectedOption === 'C' && "Mini Dashboard: Multiple KPIs at a glance in a grid layout."}
            {selectedOption === 'D' && "Rank Card: Badge-style with gradient accent highlighting the #1 position."}
            {selectedOption === 'E' && "Visual Bar: Full-width bar showing dominance with runner-up context."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default KPICardPreview;
